/**
 * Trial Request Page - Public-facing form for trial signup
 * 
 * Typeform-style progressive reveal flow:
 * 1. Child's First Name
 * 2. Child's Date of Birth (reveals recommended trial window)
 * 3. Preferred Start Date
 * 4. Parent Contact Info
 * 5. Additional Parent Toggle
 * 6. Additional Questions
 * 
 * Creates: crm_leads + crm_contacts + crm_children via trial-submit edge function
 * Sets status to "waitlisted" on submission
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Globe, 
  User, 
  Baby, 
  Phone, 
  MapPin, 
  Calendar,
  Plus,
  Minus,
  CheckCircle2,
  Loader2,
  MessageSquare,
  ChevronRight,
  ChevronLeft,
  Users,
  HelpCircle,
  Mail
} from 'lucide-react';
import { LocaleSelector } from '@/components/shared/LocaleSelector';
import { type LocaleConfig, locales, getLanguageCode } from '@/lib/locales';
import { cities, searchCities, findCityByLabel, resolveTimezone, resolveTimezoneDisplay, getCityWithTimezone, getCallingCodeForCity, getPhoneFormatExample } from '@/lib/cities';
import { useTranslation } from '@/lib/i18n';
import { glassStyles } from '@/lib/design';

// Form interfaces
interface ParentForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneSms: string;
  phoneWhatsapp: string;
  samePhone: boolean;
  city: string;
  relationToChild?: string;
  preferredLanguage?: string;
}

interface ChildForm {
  firstName: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
}

interface SurveyForm {
  referralSource: string;
  wantsCityEvent: boolean;
  certaintyLevel: string;
  additionalInfoRequest: string;
}

// Relation options
const RELATION_OPTIONS = [
  { value: 'parent', label: 'Parent' },
  { value: 'grandparent', label: 'Grandparent' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'other', label: 'Other' },
];

// Input style classes
const inputClasses = `
  w-full px-4 py-3 rounded-xl 
  bg-black/[0.03] border border-black/[0.08] 
  text-[#1C1C1E] placeholder-[#8E8E93]
  focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none
  transition-colors
`;

const selectClasses = `
  w-full px-4 py-3 rounded-xl 
  bg-black/[0.03] border border-black/[0.08] 
  text-[#1C1C1E]
  focus:ring-2 focus:ring-[#5C2D91]/30 focus:border-[#5C2D91]/50 focus:outline-none
  transition-colors appearance-none cursor-pointer
`;

const labelClasses = "block text-sm font-medium text-[#636366] mb-2";

// Default to US English
const DEFAULT_LOCALE = locales.find(l => l.locale === 'us-en')!;

// Field reveal animation wrapper
const RevealField: React.FC<{ 
  show: boolean; 
  children: React.ReactNode;
  delay?: number;
}> = ({ show, children, delay = 0 }) => {
  const [shouldRender, setShouldRender] = useState(false);
  
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => setShouldRender(true), delay);
      return () => clearTimeout(timer);
    }
  }, [show, delay]);
  
  if (!shouldRender) return null;
  
  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      {children}
    </div>
  );
};

// Calculate recommended trial window based on child's DOB
function calculateTrialWindow(birthDate: Date): { start: Date; end: Date; isOlder: boolean } {
  const thirdBirthday = new Date(birthDate);
  thirdBirthday.setFullYear(thirdBirthday.getFullYear() + 3);
  
  const threeAndHalf = new Date(thirdBirthday);
  threeAndHalf.setMonth(threeAndHalf.getMonth() + 6);
  
  const now = new Date();
  
  // If child is older than 3.5, recommend now to 3 months from now
  if (now > threeAndHalf) {
    const threeMonthsOut = new Date(now);
    threeMonthsOut.setMonth(threeMonthsOut.getMonth() + 3);
    return { start: now, end: threeMonthsOut, isOlder: true };
  }
  
  return { start: thirdBirthday, end: threeAndHalf, isOlder: false };
}

// Format date as "Month YYYY" (e.g. "June 2026")
export function formatMonthYear(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export const TrialRequestPage: React.FC = () => {
  const { t, setLanguage } = useTranslation();
  const [selectedLocale, setSelectedLocale] = useState<LocaleConfig>(DEFAULT_LOCALE);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [parent1, setParent1] = useState<ParentForm>({
    firstName: '',
    lastName: '',
    email: '',
    phoneSms: '',
    phoneWhatsapp: '',
    samePhone: true,
    city: '',
  });
  
  const [showParent2, setShowParent2] = useState(false);
  const [addParentAnswer, setAddParentAnswer] = useState<'yes' | 'no' | null>(null);
  const [parent2, setParent2] = useState<ParentForm>({
    firstName: '',
    lastName: '',
    email: '',
    phoneSms: '',
    phoneWhatsapp: '',
    samePhone: true,
    city: '',
    relationToChild: 'parent',
    preferredLanguage: 'us-en',
  });
  
  const [child, setChild] = useState<ChildForm>({
    firstName: '',
    birthDay: '',
    birthMonth: '',
    birthYear: '',
  });

  const [survey, setSurvey] = useState<SurveyForm>({
    referralSource: '',
    wantsCityEvent: false,
    certaintyLevel: '',
    additionalInfoRequest: '',
  });
  
  const [trialMonth, setTrialMonth] = useState('');
  const [trialYear, setTrialYear] = useState(new Date().getFullYear().toString());
  
  const [smsOptIn, setSmsOptIn] = useState(true);
  const [waOptIn, setWaOptIn] = useState(true);

  // City search state (primary parent)
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const filteredCities = useMemo(() => {
    if (!citySearch) return cities.slice(0, 20);
    return searchCities(citySearch).slice(0, 20);
  }, [citySearch]);

  // City search state (secondary parent)
  const [citySearch2, setCitySearch2] = useState('');
  const [showCityDropdown2, setShowCityDropdown2] = useState(false);
  const filteredCities2 = useMemo(() => {
    if (!citySearch2) return cities.slice(0, 20);
    return searchCities(citySearch2).slice(0, 20);
  }, [citySearch2]);

  // Refs for auto-scroll
  const trialDateRef = useRef<HTMLSelectElement>(null);
  const parentNameRef = useRef<HTMLInputElement>(null);
  const additionalParentRef = useRef<HTMLDivElement>(null);
  const additionalQuestionsRef = useRef<HTMLDivElement>(null);

  // Generate date options
  const days = Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
  const months = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];
  const currentYear = new Date().getFullYear();
  const birthYears = Array.from({ length: 10 }, (_, i) => ({ 
    value: String(currentYear - i), 
    label: String(currentYear - i) 
  }));
  const trialYears = [currentYear.toString(), (currentYear + 1).toString(), (currentYear + 2).toString()];

  // Calculate trial window when DOB is complete
  const trialWindow = useMemo(() => {
    if (child.birthDay && child.birthMonth && child.birthYear) {
      const birthDate = new Date(
        parseInt(child.birthYear),
        parseInt(child.birthMonth) - 1,
        parseInt(child.birthDay)
      );
      return calculateTrialWindow(birthDate);
    }
    return null;
  }, [child.birthDay, child.birthMonth, child.birthYear]);

  // Field completion checks
  const isChildNameComplete = child.firstName.trim().length > 0;
  const isDobComplete = child.birthDay && child.birthMonth && child.birthYear;
  const isTrialDateComplete = trialMonth && trialYear;
  const isContactInfoComplete = parent1.firstName && parent1.lastName && parent1.city && parent1.email && parent1.phoneSms;
  const isAdditionalParentAnswered = addParentAnswer !== null;
  const isAdditionalQuestionsVisible = isAdditionalParentAnswered && (addParentAnswer === 'no' || (addParentAnswer === 'yes' && !!parent2.firstName));

  // Required field validation for submit button
  const isFormValid = !!(
    child.firstName.trim() &&
    child.birthMonth &&
    child.birthDay &&
    child.birthYear &&
    parent1.firstName.trim() &&
    parent1.lastName.trim() &&
    parent1.email.trim() &&
    parent1.phoneSms.trim() &&
    parent1.city
  );

  // Calculate notification end date (start + 2 months)
  const notificationEndDate = useMemo(() => {
    if (!trialMonth || !trialYear) return null;
    const startMonthIndex = parseInt(trialMonth) - 1;
    const startYear = parseInt(trialYear);
    const endDate = new Date(startYear, startMonthIndex + 2, 1);
    return {
      month: months.find(m => m.value === String(endDate.getMonth() + 1))?.label || '',
      year: endDate.getFullYear()
    };
  }, [trialMonth, trialYear]);

  // Auto-update locale when primary city changes to match country calling code
  const updateLocaleForCity = (cityValue: string) => {
    const callingCode = getCallingCodeForCity(cityValue);
    if (callingCode && callingCode !== selectedLocale.callingCode) {
      const matchingLocale = locales.find(l => l.callingCode === callingCode);
      if (matchingLocale) {
        setSelectedLocale(matchingLocale);
      }
    }
  };

  // Dynamic phone placeholder — prefer city-derived calling code, fall back to locale
  const parent1CallingCode = parent1.city ? (getCallingCodeForCity(parent1.city) || selectedLocale.callingCode) : selectedLocale.callingCode;
  const phonePlaceholder = getPhoneFormatExample(parent1CallingCode);

  // Secondary parent phone placeholder (uses parent2's city calling code or falls back to locale)
  const parent2CallingCode = parent2.city ? (getCallingCodeForCity(parent2.city) || selectedLocale.callingCode) : selectedLocale.callingCode;
  const phonePlaceholder2 = getPhoneFormatExample(parent2CallingCode);
  
  // Resolved timezone for secondary parent
  const resolvedTimezone2 = parent2.city ? resolveTimezone(parent2.city) : '';
  const resolvedTimezoneLabel2 = resolvedTimezone2 ? resolveTimezoneDisplay(resolvedTimezone2) : '';

  // Handle locale selection
  const handleLocaleSelect = (locale: LocaleConfig) => {
    setSelectedLocale(locale);
    setLanguage(getLanguageCode(locale.locale));
  };

  // Resolve timezone: known city slug → mapped tz, otherwise browser-detected tz
  const resolvedTimezone = parent1.city ? resolveTimezone(parent1.city) : '';
  const resolvedTimezoneLabel = resolvedTimezone ? resolveTimezoneDisplay(resolvedTimezone) : '';

  // Format phone number to E.164
  const formatPhone = (phone: string): string | null => {
    if (!phone) return null;
    const trimmed = phone.trim();
    if (!trimmed) return null;
    
    if (trimmed.startsWith('+')) {
      const digits = trimmed.slice(1).replace(/\D/g, '');
      return digits ? `+${digits}` : null;
    }
    
    if (trimmed.startsWith('00')) {
      const digits = trimmed.slice(2).replace(/\D/g, '');
      return digits ? `+${digits}` : null;
    }
    
    const digits = trimmed.replace(/\D/g, '');
    if (!digits) return null;
    
    const callingCode = selectedLocale.callingCode;
    return `+${callingCode}${digits}`;
  };

  // Auto-scroll to newly revealed sections
  useEffect(() => {
    if (isDobComplete && trialDateRef.current) {
      setTimeout(() => trialDateRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [isDobComplete]);

  useEffect(() => {
    if (isTrialDateComplete && parentNameRef.current) {
      setTimeout(() => parentNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [isTrialDateComplete]);

  useEffect(() => {
    if (isContactInfoComplete && additionalParentRef.current) {
      setTimeout(() => additionalParentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [isContactInfoComplete]);

  useEffect(() => {
    if (isAdditionalQuestionsVisible && additionalQuestionsRef.current) {
      setTimeout(() => additionalQuestionsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [isAdditionalQuestionsVisible]);

  // Submit handler
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const notBeforeDate = trialMonth && trialYear 
        ? `${trialYear}-${trialMonth.padStart(2, '0')}-01`
        : null;

      const payload = {
        source: 'website',
        referralSource: survey.referralSource || null,
        preferredLanguage: selectedLocale?.locale || 'us-en',
        preferredTrialMonth: trialMonth ? parseInt(trialMonth) : null,
        preferredTrialYear: trialYear ? parseInt(trialYear) : null,
        notBeforeDate,
        surveyResponses: {
          referral_source: survey.referralSource,
          wants_city_event: survey.wantsCityEvent,
          certainty_level: survey.certaintyLevel,
          additional_info_request: survey.additionalInfoRequest,
        },
        
        primaryContact: {
          firstName: parent1.firstName,
          lastName: parent1.lastName,
          email: parent1.email,
          phoneSms: formatPhone(parent1.phoneSms),
          phoneWhatsapp: parent1.samePhone 
            ? formatPhone(parent1.phoneSms)
            : formatPhone(parent1.phoneWhatsapp),
          city: parent1.city,
          timezone: resolveTimezone(parent1.city),
          preferredLanguage: selectedLocale?.locale || 'us-en',
          smsOptIn,
          waOptIn,
        },
        
        secondaryContact: addParentAnswer === 'yes' && parent2.firstName ? {
          firstName: parent2.firstName,
          lastName: parent2.lastName,
          email: parent2.email || null,
          phoneSms: formatPhone(parent2.phoneSms),
          phoneWhatsapp: parent2.samePhone
            ? formatPhone(parent2.phoneSms)
            : formatPhone(parent2.phoneWhatsapp),
          city: parent2.city,
          timezone: parent2.city ? resolveTimezone(parent2.city) : null,
          relationToChild: parent2.relationToChild,
          preferredLanguage: parent2.preferredLanguage || 'us-en',
        } : null,
        
        child: {
          firstName: child.firstName,
          birthDay: child.birthDay ? parseInt(child.birthDay) : null,
          birthMonth: child.birthMonth ? parseInt(child.birthMonth) : null,
          birthYear: child.birthYear ? parseInt(child.birthYear) : null,
        },
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/trial-submit`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit');
      }

      setSubmitted(true);

    } catch (err) {
      console.error('Error submitting trial request:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success state
  if (submitted) {
    const selectedMonth = months.find(m => m.value === trialMonth)?.label || '';
    // Calculate end date (start + 2 months)
    const startMonthIndex = parseInt(trialMonth || '1') - 1;
    const startYear = parseInt(trialYear || String(currentYear));
    const endDate = new Date(startYear, startMonthIndex + 2, 1);
    const endMonth = months.find(m => m.value === String(endDate.getMonth() + 1))?.label || '';
    const endYear = endDate.getFullYear();
    
    return (
      <div className="min-h-screen bg-[#FAF6F0] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center p-8 rounded-2xl" style={glassStyles.card}>
          <div className="w-16 h-16 bg-[#34C759]/15 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#34C759]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1C1C1E] mb-4">
            {t('Thank You')}, {parent1.firstName}!
          </h1>
          <p className="text-[#636366] mb-6">
            {t('We look forward to welcoming you to Turn soon. We will notify you between')} <strong className="text-[#1C1C1E]">{selectedMonth} {trialYear}</strong> {t('and')} <strong className="text-[#1C1C1E]">{endMonth} {endYear}</strong> {t('when a seat is available for')} {child.firstName}'s {t('trial')}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF6F0] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header with Language Selector */}
        <div className="mb-8">
          <div className="flex justify-end mb-6">
            <LocaleSelector 
              value={selectedLocale.locale}
              onChange={handleLocaleSelect}
            />
          </div>
          
          <div className="text-center">
            <h1 className="text-3xl font-semibold text-[#1C1C1E] mb-2">
              {t("It's your turn")}
            </h1>
            <p className="text-[#636366]">
              {t("We're so excited to welcome you to Turn. Please complete the form below to register your child's spot.")}
            </p>
          </div>
        </div>

        {/* Single-page typeform-style form */}
        <div className="rounded-2xl p-6 space-y-8" style={glassStyles.card}>
          
          {/* Section 1: Child's First Name */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-[#636366] text-sm">
              <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">1</span>
              <span>{t("Your child's first name")}</span>
            </div>
            <div>
              <input
                type="text"
                value={child.firstName}
                onChange={e => setChild({ ...child, firstName: e.target.value })}
                className={`${inputClasses} text-lg`}
                placeholder={t("Enter first name...")}
                autoFocus
              />
            </div>
          </div>

          {/* Section 2: Child's Date of Birth - reveals after name */}
          <RevealField show={isChildNameComplete} delay={200}>
            <div className="space-y-4 pt-4 border-t border-black/[0.08]">
              <div className="flex items-center gap-2 text-[#636366] text-sm">
                <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">2</span>
                <span>{child.firstName}'s {t("date of birth")}</span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <select
                    value={child.birthMonth}
                    onChange={e => setChild({ ...child, birthMonth: e.target.value })}
                    className={selectClasses}
                  >
                    <option value="" disabled>{t('Month')}</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{t(m.label)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={child.birthDay}
                    onChange={e => setChild({ ...child, birthDay: e.target.value })}
                    className={selectClasses}
                  >
                    <option value="" disabled>{t('Day')}</option>
                    {days.map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={child.birthYear}
                    onChange={e => setChild({ ...child, birthYear: e.target.value })}
                    className={selectClasses}
                  >
                    <option value="" disabled>{t('Year')}</option>
                    {birthYears.map(y => (
                      <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </RevealField>

          {/* Section 3: Preferred Start Date - reveals after DOB with recommendation */}
          <RevealField show={!!isDobComplete} delay={200}>
            <div className="space-y-4 pt-4 border-t border-black/[0.08]">
              <div className="flex items-center gap-2 text-[#636366] text-sm">
                <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">3</span>
                <span>{t("Preferred Start Date")}</span>
              </div>
              
              {/* Trial window recommendation message */}
              {trialWindow && (
                <div className="p-3 rounded-xl bg-[#34C759]/10 border border-[#34C759]/20">
                  <p className="text-sm text-[#34C759]">
                    {t("We recommend starting")} {child.firstName}'s {t("trial between")} <strong>{formatMonthYear(trialWindow.start)}</strong> {t("and")} <strong>{formatMonthYear(trialWindow.end)}</strong>.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <select
                    ref={trialDateRef}
                    value={trialMonth}
                    onChange={e => setTrialMonth(e.target.value)}
                    className={selectClasses}
                  >
                    <option value="" disabled>{t('Month')}</option>
                    {months.map(m => (
                      <option key={m.value} value={m.value}>{t(m.label)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <select
                    value={trialYear}
                    onChange={e => setTrialYear(e.target.value)}
                    className={selectClasses}
                  >
                    {trialYears.map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </RevealField>

          {/* Section 4: Contact Info - reveals after trial date */}
          <RevealField show={!!isTrialDateComplete} delay={200}>
            <div className="space-y-4 pt-4 border-t border-black/[0.08]">
              {/* Notification message */}
              <div className="p-3 rounded-xl bg-[#5C2D91]/10 border border-[#5C2D91]/20">
                <p className="text-sm text-[#1C1C1E]">
                  {t("We will notify you between")} <strong className="text-[#1C1C1E]">{months.find(m => m.value === trialMonth)?.label} {trialYear}</strong> {t("and")} <strong className="text-[#1C1C1E]">{notificationEndDate?.month} {notificationEndDate?.year}</strong> {t("when a seat is available for")} {child.firstName}. {t("Please tell us how to reach you:")}
                </p>
              </div>
              
              <div className="flex items-center gap-2 text-[#636366] text-sm">
                <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">4</span>
                <span>{t("Your contact information")}</span>
              </div>
              
              {/* Parent Name */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClasses}>{t('Your First Name')} *</label>
                  <input
                    ref={parentNameRef}
                    type="text"
                    value={parent1.firstName}
                    onChange={e => setParent1({ ...parent1, firstName: e.target.value })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className={labelClasses}>{t('Your Last Name')} *</label>
                  <input
                    type="text"
                    value={parent1.lastName}
                    onChange={e => setParent1({ ...parent1, lastName: e.target.value })}
                    className={inputClasses}
                  />
                </div>
              </div>

              {/* City with timezone */}
              <div className="relative">
                <label className={labelClasses}>{t('City and Time Zone')} *</label>
                <input
                  type="text"
                  value={citySearch}
                  onChange={e => {
                    const val = e.target.value;
                    setCitySearch(val);
                    setShowCityDropdown(true);
                    const known = findCityByLabel(val);
                    setParent1({ ...parent1, city: known ? known.value : val.trim() });
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowCityDropdown(false), 150);
                    if (citySearch.trim() && !parent1.city) {
                      setParent1({ ...parent1, city: citySearch.trim() });
                    }
                  }}
                  onFocus={() => setShowCityDropdown(true)}
                  className={inputClasses}
                  placeholder={t('Start typing your city...')}
                />
                {showCityDropdown && citySearch && filteredCities.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-xl glass-elevated border border-black/[0.08] shadow-lg">
                    {filteredCities.map(city => (
                      <button
                        key={city.value}
                        type="button"
                        onClick={() => {
                          setParent1({ ...parent1, city: city.value });
                          setCitySearch(city.label);
                          setShowCityDropdown(false);
                          updateLocaleForCity(city.value);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-[#1C1C1E] hover:bg-black/[0.04] transition-colors first:rounded-t-xl last:rounded-b-xl"
                      >
                        {getCityWithTimezone(city.value)}
                      </button>
                    ))}
                  </div>
                )}
                {parent1.city && resolvedTimezoneLabel && (
                  <p className="mt-1 text-xs text-[#8E8E93]">
                    {resolvedTimezoneLabel}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className={labelClasses}>{t('Email Address')} *</label>
                <input
                  type="email"
                  value={parent1.email}
                  onChange={e => setParent1({ ...parent1, email: e.target.value })}
                  className={inputClasses}
                  placeholder="you@example.com"
                />
              </div>

              {/* Phone SMS */}
              <div>
                <label className={labelClasses}>{t('Phone Number')} *</label>
                <input
                  type="tel"
                  value={parent1.phoneSms}
                  onChange={e => setParent1({ ...parent1, phoneSms: e.target.value })}
                  placeholder={phonePlaceholder}
                  className={inputClasses}
                />
              </div>

              {/* Phone WhatsApp */}
              <div>
                <label className={labelClasses}>{t('WhatsApp Phone Number')}</label>
                <label className="flex items-center gap-2 text-sm text-[#636366] mb-2">
                  <input
                    type="checkbox"
                    checked={parent1.samePhone}
                    onChange={e => setParent1({ ...parent1, samePhone: e.target.checked })}
                    className="rounded border-black/[0.12] bg-black/[0.03] text-[#5C2D91] focus:ring-[#5C2D91]/30"
                  />
                  {t('Same as phone number above')}
                </label>
                
                {!parent1.samePhone && (
                  <input
                    type="tel"
                    value={parent1.phoneWhatsapp}
                    onChange={e => setParent1({ ...parent1, phoneWhatsapp: e.target.value })}
                    placeholder={phonePlaceholder}
                    className={inputClasses}
                  />
                )}
              </div>
            </div>
          </RevealField>

          {/* Section 5: Additional Parent Toggle - reveals after contact info */}
          <RevealField show={!!isContactInfoComplete} delay={200}>
            <div ref={additionalParentRef} className="space-y-4 pt-4 border-t border-black/[0.08]">
              <div className="flex items-center gap-2 text-[#636366] text-sm">
                <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">5</span>
                <span>{t("Would you like to add an additional parent for notifications?")}</span>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setAddParentAnswer('yes');
                    setShowParent2(true);
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                    addParentAnswer === 'yes' 
                      ? 'border-[#5C2D91] bg-[#5C2D91]/10 text-[#1C1C1E]' 
                      : 'border-black/[0.08] text-[#636366] hover:border-black/[0.12]'
                  }`}
                >
                  {t('Yes')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddParentAnswer('no');
                    setShowParent2(false);
                  }}
                  className={`flex-1 px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                    addParentAnswer === 'no' 
                      ? 'border-[#5C2D91] bg-[#5C2D91]/10 text-[#1C1C1E]' 
                      : 'border-black/[0.08] text-[#636366] hover:border-black/[0.12]'
                  }`}
                >
                  {t('No')}
                </button>
              </div>

              {/* Secondary parent form - reveals if Yes */}
              {showParent2 && addParentAnswer === 'yes' && (
                <div className="space-y-4 p-4 rounded-xl bg-black/[0.03] animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-4 h-4 text-[#636366]" />
                    <span className="text-sm font-medium text-[#1C1C1E]">{t('Additional Parent/Caretaker')}</span>
                  </div>

                  <div>
                    <label className={labelClasses}>{t('Relation to')} {child.firstName}</label>
                    <select
                      value={parent2.relationToChild}
                      onChange={e => setParent2({ ...parent2, relationToChild: e.target.value })}
                      className={selectClasses}
                    >
                      {RELATION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.label)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClasses}>{t('First Name')}</label>
                      <input
                        type="text"
                        value={parent2.firstName}
                        onChange={e => setParent2({ ...parent2, firstName: e.target.value })}
                        className={inputClasses}
                      />
                    </div>
                    <div>
                      <label className={labelClasses}>{t('Last Name')}</label>
                      <input
                        type="text"
                        value={parent2.lastName}
                        onChange={e => setParent2({ ...parent2, lastName: e.target.value })}
                        className={inputClasses}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelClasses}>{t('Email Address')}</label>
                    <input
                      type="email"
                      value={parent2.email}
                      onChange={e => setParent2({ ...parent2, email: e.target.value })}
                      className={inputClasses}
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="relative">
                    <label className={labelClasses}>{t('City and Time Zone')}</label>
                    <input
                      type="text"
                      value={citySearch2}
                      onChange={e => {
                        const val = e.target.value;
                        setCitySearch2(val);
                        setShowCityDropdown2(true);
                        const known = findCityByLabel(val);
                        setParent2({ ...parent2, city: known ? known.value : val.trim() });
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowCityDropdown2(false), 150);
                        if (citySearch2.trim() && !parent2.city) {
                          setParent2({ ...parent2, city: citySearch2.trim() });
                        }
                      }}
                      onFocus={() => setShowCityDropdown2(true)}
                      className={inputClasses}
                      placeholder={t('Start typing your city...')}
                    />
                    {showCityDropdown2 && citySearch2 && filteredCities2.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto rounded-xl glass-elevated border border-black/[0.08] shadow-lg">
                        {filteredCities2.map(city => (
                          <button
                            key={city.value}
                            type="button"
                            onClick={() => {
                              setParent2({ ...parent2, city: city.value });
                              setCitySearch2(city.label);
                              setShowCityDropdown2(false);
                            }}
                            className="w-full px-4 py-2.5 text-left text-sm text-[#1C1C1E] hover:bg-black/[0.04] transition-colors first:rounded-t-xl last:rounded-b-xl"
                          >
                            {getCityWithTimezone(city.value)}
                          </button>
                        ))}
                      </div>
                    )}
                    {parent2.city && resolvedTimezoneLabel2 && (
                      <p className="mt-1 text-xs text-[#8E8E93]">
                        {resolvedTimezoneLabel2}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={labelClasses}>{t('Phone Number (SMS)')}</label>
                    <input
                      type="tel"
                      value={parent2.phoneSms}
                      onChange={e => setParent2({ ...parent2, phoneSms: e.target.value })}
                      placeholder={phonePlaceholder2}
                      className={inputClasses}
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-sm text-[#636366] mb-2">
                      <input
                        type="checkbox"
                        checked={parent2.samePhone}
                        onChange={e => setParent2({ ...parent2, samePhone: e.target.checked })}
                        className="rounded border-black/[0.12] bg-black/[0.03] text-[#5C2D91] focus:ring-[#5C2D91]/30"
                      />
                      {t('Same number for WhatsApp')}
                    </label>
                    
                    {!parent2.samePhone && (
                      <div className="mt-2">
                        <label className={labelClasses}>{t('Phone Number (WhatsApp)')}</label>
                        <input
                          type="tel"
                          value={parent2.phoneWhatsapp}
                          onChange={e => setParent2({ ...parent2, phoneWhatsapp: e.target.value })}
                          placeholder={phonePlaceholder2}
                          className={inputClasses}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={labelClasses}>{t('Preferred Language')}</label>
                    <select
                      value={parent2.preferredLanguage}
                      onChange={e => setParent2({ ...parent2, preferredLanguage: e.target.value })}
                      className={selectClasses}
                    >
                      {locales.map(l => (
                        <option key={l.locale} value={l.locale}>
                          {l.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </RevealField>

          {/* Section 6: Additional Questions - reveals after parent decision */}
          <RevealField show={isAdditionalQuestionsVisible} delay={200}>
            <div ref={additionalQuestionsRef} className="space-y-6 pt-4 border-t border-black/[0.08]">
              <div className="flex items-center gap-2 text-[#636366] text-sm">
                <span className="w-6 h-6 rounded-full bg-[#5C2D91]/20 text-[#5C2D91] flex items-center justify-center text-xs font-medium">6</span>
                <span>{t("Additional Questions")}</span>
              </div>

              {/* CEO Events */}
              <label className="flex items-start gap-3 p-4 rounded-xl border border-black/[0.08] hover:border-black/[0.12] cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={survey.wantsCityEvent}
                  onChange={e => setSurvey({ ...survey, wantsCityEvent: e.target.checked })}
                  className="mt-1 rounded border-black/[0.12] bg-black/[0.03] text-[#5C2D91] focus:ring-[#5C2D91]/30"
                />
                <div>
                  <p className="font-medium text-[#1C1C1E]">{t('Event Invitations')}</p>
                  <p className="text-sm text-[#8E8E93]">
                    {t('Our CEO, Addison Hoffman, travels to over 20 cities each year to visit current Turn families, Turn alumni, and prospective Turn families. Would you like to receive an invitation to an event in')} {citySearch || t('your city')}?
                  </p>
                </div>
              </label>

              {/* Certainty Level */}
              <div>
                <label className={labelClasses}>
                  {t('How certain are you that Turn is the best program to ensure')} {child.firstName} {t('turns into a reader?')}
                </label>
                <textarea
                  value={survey.certaintyLevel}
                  onChange={e => setSurvey({ ...survey, certaintyLevel: e.target.value })}
                  rows={3}
                  className={inputClasses}
                  placeholder={t('Share your thoughts...')}
                />
              </div>

              {/* Additional Info */}
              <div>
                <label className={labelClasses}>
                  {t("Ahead of")} {child.firstName}'s {t("trial, what other information would you like to receive about our program?")}
                </label>
                <textarea
                  value={survey.additionalInfoRequest}
                  onChange={e => setSurvey({ ...survey, additionalInfoRequest: e.target.value })}
                  rows={3}
                  className={inputClasses}
                  placeholder={t("Let us know what you'd like to learn more about...")}
                />
              </div>

              {error && (
                <div className="p-4 bg-[#FF3B30]/10 border border-[#FF3B30]/20 rounded-xl text-[#FF3B30] text-sm">
                  {error}
                </div>
              )}

              <p className="text-xs text-[#8E8E93]">
                {t('By submitting, you agree to our')}{' '}
                <a href="/terms" className="text-[#5C2D91] hover:underline">{t('Terms of Service')}</a>
                {' '}{t('and')}{' '}
                <a href="/privacy" className="text-[#5C2D91] hover:underline">{t('Privacy Policy')}</a>.
              </p>

              <button
                onClick={handleSubmit}
                disabled={!isFormValid || submitting}
                className="w-full px-6 py-4 bg-[#5C2D91] hover:bg-[#5C2D91]/90 disabled:bg-black/[0.05] disabled:text-[#C7C7CC] text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors text-lg"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('Submitting...')}
                  </>
                ) : (
                  t('Submit Request')
                )}
              </button>
              {!isFormValid && (
                <p className="text-sm text-[#8E8E93] text-center mt-2">
                  Please fill in all required fields to continue.
                </p>
              )}
            </div>
          </RevealField>
        </div>

        <p className="text-center text-sm text-[#8E8E93] mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#5C2D91] hover:text-[#5C2D91]/80 transition-colors">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default TrialRequestPage;
